// M141 — /pagina-focus — Top-10 pagina's op basis van verbeter-kans.
// Toont per pagina 3 badges (JustEasySEO, Belang, Verbeterkans) + de reden
// in mensentaal waarom deze pagina in de lijst staat.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { Loader2, ExternalLink, Target } from "lucide-react";

function Badge({ label, value, tone = "muted" }) {
  const cls = tone === "teal"
    ? "border-accent-teal/40 text-accent-teal bg-accent-teal/10"
    : tone === "amber"
      ? "border-warn-amber/40 text-warn-amber bg-warn-amber/10"
      : "border-border-hairline text-text-muted";
  return (
    <div className={`px-2 py-1 rounded border text-center ${cls}`}>
      <div className="text-[9px] leading-none">{label}</div>
      <div className="text-sm font-display leading-tight mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

export default function PaginaFocusPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/pages/top10"); setData(data); }
      catch (e) { setErr(e?.response?.data?.detail?.reason_nl || "Kon pagina's niet ophalen."); }
    })();
  }, []);
  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
               data-testid="pagina-focus-title">
            <Target className="w-5 h-5 text-accent-teal" /> Pagina-focus — top 10
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Adviesmodus — geselecteerd op verbeter-kans (belang × verbeterruimte).
          </p>
          <p className="text-sm text-text-muted mt-2 italic" data-testid="pagina-focus-hint">
            Deze 10 pagina's hebben de meeste verbeter-kans. JustEasySEO kiest ze elke 4 weken opnieuw.
          </p>
        </div>
      )}
      {hideInnerHeader && (
        <p className="text-sm text-text-muted mb-4 italic" data-testid="pagina-focus-hint">
          Deze 10 pagina's hebben de meeste verbeter-kans. JustEasySEO kiest ze elke 4 weken opnieuw.
        </p>
      )}
      {err && <div className="text-xs text-warn-amber italic mb-4">{err}</div>}
      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pagina's laden…
        </div>
      ) : (
        <>
          {data.note_nl && <p className="text-[11px] text-text-subtle italic mb-3">{data.note_nl}</p>}
          <div className="space-y-2" data-testid="pagina-focus-list">
            {(data.items || []).map((it, i) => (
              <div key={it.page_id} data-testid={`pagina-focus-row-${i}`}
                    className="rounded-card border border-border-hairline bg-bg-panel px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-text-primary font-medium truncate">{it.title || it.url}</div>
                    <a href={it.url} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-accent-teal truncate inline-flex items-center gap-1 hover:underline">
                      {it.url} <ExternalLink className="w-3 h-3" />
                    </a>
                    {it.selection_reason && (
                      <div data-testid={`pagina-focus-reason-${i}`}
                            className="text-[11px] text-text-muted mt-1 italic">
                        {it.selection_reason}
                      </div>
                    )}
                    {it.target_keywords?.length > 0 && (
                      <div className="text-[10px] text-text-muted mt-1">
                        Zoekwoorden: {it.target_keywords.join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-stretch gap-2 shrink-0">
                    <Badge label="JustEasySEO" value={it.nexora_score} tone="teal" />
                    <Badge label="Belang" value={it.importance_score} />
                    <Badge label="Verbeterkans" value={it.opportunity_score} tone="amber" />
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <a href={`/adviezen?page=${it.page_id}`}
                      className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10"
                      data-testid={`pagina-focus-advice-${i}`}>
                    Adviezen
                  </a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AppLayout>
  );
}
